/**
 * ManageSubscriptionPage
 * Customer-facing page for viewing subscription details and upgrading plans.
 *
 * - Demo users: see upgrade cards to Developer/PlanA/PlanB
 * - Developer users: see usage bars + billing management + upgrade to PlanA/PlanB
 * - PlanA/PlanB users: see usage bars + billing management
 *
 * Layout follows PortalLayout pattern (header, sidebar, footer).
 * UI/UX follows Pro Max design system: SaaS trust-blue palette, dimensional
 * layering, spinner loading indicator, accessible contrast ratios.
 */

import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { formatDateByPrefs } from '@utils/format-locale';
import { PortalLayout } from '@/components/common/PortalLayout';
import { useUserPreferences } from '@/components/features/settings/UserSettings';
import { useManageSubscription } from './ManageSubscriptionPage.hook';

// ============================================================================
// Loading Indicator Component
// ============================================================================

interface LoadingStateProps {
  loadingText: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({ loadingText }) => (
  <div className="flex items-center justify-center py-20">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 border-[3px] border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground m-0">{loadingText}</p>
    </div>
  </div>
);

// ============================================================================
// Error Component
// ============================================================================

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
  errorTitle: string;
  retryText: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, errorTitle, retryText }) => (
  <div className="flex items-center justify-center py-20 bg-background">
    <div className="flex flex-col items-center gap-4 p-6 max-w-md text-center">
      <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
        <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold">{errorTitle}</h2>
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        {retryText}
      </button>
    </div>
  </div>
);

// ============================================================================
// Sub-Components
// ============================================================================

/** Usage progress bar with percentage label and color thresholds */
function UsageBar({
  label,
  current,
  max,
}: {
  label: string;
  current: number;
  max: number;
}) {
  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const colorClass =
    pct >= 90
      ? 'bg-red-500'
      : pct >= 70
        ? 'bg-amber-500'
        : 'bg-emerald-500';
  const labelColor =
    pct >= 90
      ? 'text-red-600 dark:text-red-400'
      : pct >= 70
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400';

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex justify-between items-baseline mb-1.5">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-xs font-semibold tabular-nums ${labelColor}`}>
          {current.toLocaleString()} / {max.toLocaleString()} ({Math.round(pct)}%)
        </span>
      </div>
      <div className="h-2.5 bg-white border border-secondary/50 shadow-inner rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${colorClass}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={`${label}: ${current} of ${max} used`}
        />
      </div>
    </div>
  );
}

/** Bold the numeric value inside a translated label string (e.g. "Up to 10 Mailboxes" → bold "10") */
function boldNumber(label: string, value: number): React.ReactNode {
  const numStr = String(value);
  const idx = label.indexOf(numStr);
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <strong className="font-bold text-foreground">{numStr}</strong>
      {label.slice(idx + numStr.length)}
    </>
  );
}

/** Checkmark icon for feature list */
function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

/** Upgrade plan card with hover lift and CTA button */
function UpgradeCard({
  planId,
  displayName,
  mailboxLimit,
  kcLimit,
  priceLabel,
  kcOverageRate,
  upgrading,
  onUpgrade,
  mailboxesLabel,
  kcsLabel,
  kcOverageLabel,
  fullApiAccessLabel,
  whiteLabelLabel,
  realTimeTrackingLabel,
  processingText,
  tryNowText,
  upgradeNowText,
}: {
  planId: string;
  displayName: string;
  mailboxLimit: number;
  kcLimit: number;
  priceLabel: string;
  kcOverageRate: number;
  upgrading: boolean;
  onUpgrade: (planId: string) => void;
  mailboxesLabel: string;
  kcsLabel: string;
  kcOverageLabel: string;
  fullApiAccessLabel: string;
  whiteLabelLabel: string;
  realTimeTrackingLabel: string;
  processingText: string;
  tryNowText: string;
  upgradeNowText: string;
}) {
  return (
    <div className="group relative border border-border rounded-xl p-6 bg-card shadow-sm flex flex-col gap-4 transition-all duration-200 hover:shadow-md hover:border-primary/30 cursor-pointer">
      {/* Card header */}
      <div>
        <h3 className="text-base font-semibold m-0 mb-1">{displayName}</h3>
        <div className="text-2xl font-bold text-primary">{priceLabel}</div>
      </div>

      {/* Feature list */}
      <ul className="flex-1 space-y-2 text-sm text-muted-foreground list-none p-0 m-0">
        <li className="flex items-center gap-2">
          <CheckIcon />
          {boldNumber(mailboxesLabel, mailboxLimit)}
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon />
          {boldNumber(kcsLabel, kcLimit)}
        </li>
        {kcOverageRate > 0 && (
          <li className="flex items-center gap-2">
            <CheckIcon />
            {kcOverageLabel}{' '}
            <strong className="font-bold text-foreground">${kcOverageRate.toFixed(2)}/KC</strong>
          </li>
        )}
        <li className="flex items-center gap-2">
          <CheckIcon />
          {fullApiAccessLabel}
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon />
          {whiteLabelLabel}
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon />
          {realTimeTrackingLabel}
        </li>
      </ul>

      {/* CTA button */}
      <button
        onClick={() => onUpgrade(planId)}
        disabled={upgrading}
        className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 hover:bg-primary/90 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center justify-center gap-2"
      >
        {upgrading && (
          <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        )}
        {upgrading ? processingText : planId === 'Developer' ? tryNowText : upgradeNowText}
      </button>
    </div>
  );
}

/** Current plan card — read-only, shown alongside upgrade options for quick comparison */
function CurrentPlanCard({
  displayName,
  priceLabel,
  mailboxLimit,
  kcLimit,
  kcOverageRate,
  hasApiAccess,
  hasWhiteLabel,
  hasRealTimeTracking,
  currentBadgeText,
  mailboxesLabel,
  kcsLabel,
  kcOverageLabel,
  fullApiAccessLabel,
  whiteLabelLabel,
  realTimeTrackingLabel,
}: {
  displayName: string;
  priceLabel: string;
  mailboxLimit: number;
  kcLimit: number;
  kcOverageRate: number;
  hasApiAccess: boolean;
  hasWhiteLabel: boolean;
  hasRealTimeTracking: boolean;
  currentBadgeText: string;
  mailboxesLabel: string;
  kcsLabel: string;
  kcOverageLabel: string;
  fullApiAccessLabel: string;
  whiteLabelLabel: string;
  realTimeTrackingLabel: string;
}) {
  return (
    <div className="relative border-2 border-primary/40 rounded-xl p-6 bg-primary/[0.03] shadow-sm flex flex-col gap-4">
      {/* Current plan badge */}
      <div className="absolute -top-3 left-4">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground">
          {currentBadgeText}
        </span>
      </div>

      {/* Card header */}
      <div>
        <h3 className="text-base font-semibold m-0 mb-1">{displayName}</h3>
        {priceLabel && <div className="text-2xl font-bold text-primary">{priceLabel}</div>}
      </div>

      {/* Feature list */}
      <ul className="flex-1 space-y-2 text-sm text-muted-foreground list-none p-0 m-0">
        <li className="flex items-center gap-2">
          <CheckIcon />
          {boldNumber(mailboxesLabel, mailboxLimit)}
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon />
          {boldNumber(kcsLabel, kcLimit)}
        </li>
        {kcOverageRate > 0 && (
          <li className="flex items-center gap-2">
            <CheckIcon />
            {kcOverageLabel}{' '}
            <strong className="font-bold text-foreground">${kcOverageRate.toFixed(2)}/KC</strong>
          </li>
        )}
        {hasApiAccess && (
          <li className="flex items-center gap-2">
            <CheckIcon />
            {fullApiAccessLabel}
          </li>
        )}
        {hasWhiteLabel && (
          <li className="flex items-center gap-2">
            <CheckIcon />
            {whiteLabelLabel}
          </li>
        )}
        {hasRealTimeTracking && (
          <li className="flex items-center gap-2">
            <CheckIcon />
            {realTimeTrackingLabel}
          </li>
        )}
      </ul>
    </div>
  );
}

/** Section card wrapper for consistent styling */
function SectionCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`border border-border rounded-xl p-6 bg-card shadow-sm ${className ?? ''}`}>
      {children}
    </div>
  );
}

/** Section heading for consistent typography */
function SectionHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h3 className={`text-base font-semibold m-0 ${className ?? ''}`}>
      {children}
    </h3>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const ManageSubscriptionPage = forwardRef<HTMLDivElement>((_props, ref) => {
  const { t } = useTranslation(NS.MANAGE_SUBSCRIPTION);
  const {
    data,
    loading,
    error,
    upgrading,
    upgradeError,
    refetch,
    initiateUpgrade,
  } = useManageSubscription();
  const { dateFormat, timezoneOffset } = useUserPreferences();

  const formattedTrialEndDate = useMemo(() => {
    if (!data?.trialExpireDate) return '';
    return formatDateByPrefs(data.trialExpireDate, dateFormat, timezoneOffset);
  }, [data?.trialExpireDate, dateFormat, timezoneOffset]);

  // Loading state — skeleton UI
  if (loading) {
    return (
      <PortalLayout ref={ref} contentClassName="overflow-x-hidden">
        <LoadingState loadingText={t('ManageSubscription_Loading', 'Loading subscription...')} />
      </PortalLayout>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <PortalLayout ref={ref} contentClassName="overflow-x-hidden">
        <ErrorState
          error={error ?? new Error(t('ManageSubscription_ErrorDefault', 'Failed to load subscription information.'))}
          onRetry={refetch}
          errorTitle={t('ManageSubscription_ErrorTitle', 'Failed to load subscription')}
          retryText={t('ManageSubscription_Retry', 'Try Again')}
        />
      </PortalLayout>
    );
  }

  const hasUpgradeOptions = data.upgradeOptions && data.upgradeOptions.length > 0;
  // Backend normalizes PlanB → ProPlus before serializing, so only ProPlus check is needed
  const isProPlus = data.currentPlan === 'ProPlus';
  const subscriptionState = data.subscriptionState ?? (data.isTrial ? 'Trial' : 'Active');

  // Determine plan status badge
  const statusBadge = (() => {
    switch (subscriptionState) {
      case 'Trial':
        return { label: t('ManageSubscription_StatusTrial', 'Trial'), className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
      case 'Expired':
        return { label: t('ManageSubscription_StatusExpired', 'Expired'), className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
      case 'Blocked':
        return { label: t('ManageSubscription_StatusBlocked', 'Suspended'), className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
      default:
        return { label: t('ManageSubscription_StatusActive', 'Active'), className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' };
    }
  })();

  return (
    <PortalLayout ref={ref} contentClassName="overflow-x-hidden">
      <div className="p-4 sm:p-6 space-y-6">
        {/* ── Page Header ── */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-accent">
            {t('ManageSubscription_Title', 'Manage Subscription')}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('ManageSubscription_Description', 'View your current plan, usage, and upgrade options.')}
          </p>
        </div>

        {/* ── Subscription Status Banner ── */}
        {subscriptionState === 'Trial' && (
          <div className="flex items-start gap-3 p-4 rounded-lg text-sm border bg-primary/5 border-primary/20 text-primary" role="alert">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <p className="m-0">
              {t('ManageSubscription_TrialBanner_Body', 'Your free trial ends on {{trialEndDate}}. To continue using ECGrid VAN Developer after that date, add a payment method before your trial expires. If no payment method is added, your access will be suspended when the trial ends.', { trialEndDate: formattedTrialEndDate })}
            </p>
          </div>
        )}
        {subscriptionState === 'Expired' && (
          <div className="flex items-start gap-3 p-4 rounded-lg text-sm border bg-destructive/5 border-destructive/20 text-destructive" role="alert">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            <p className="m-0">
              {t('ManageSubscription_ExpiredBanner_Body', 'Your free trial ended on {{trialEndDate}}. Your account is currently suspended and API access has been paused. To restore access, add a payment method to continue on the Developer plan or select a higher tier below. You can also cancel your account if you no longer need access.', { trialEndDate: formattedTrialEndDate })}
            </p>
          </div>
        )}
        {subscriptionState === 'Blocked' && (
          <div className="flex items-start gap-3 p-4 rounded-lg text-sm border bg-destructive/5 border-destructive/20 text-destructive" role="alert">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <p className="m-0">
              {t('ManageSubscription_BlockedBanner_Body', 'Your subscription has been cancelled and access to your account has been suspended. To restore access, please select a new plan below or contact our support team.')}
            </p>
          </div>
        )}

        {/* ── Upgrade Error ── */}
        {upgradeError && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive text-sm" role="alert">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
            {upgradeError.message}
          </div>
        )}

        {/* ── Current Plan Details ── */}
        <SectionCard>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-border">
            <SectionHeading className="flex items-center gap-2">
              {t('ManageSubscription_CurrentPlan', 'Current Plan:')} <span className="text-primary">{data.planDisplayName}</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            </SectionHeading>
            {(data.planStartDate || data.periodEndDate) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground tabular-nums">
                {data.planStartDate && (
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    {t('ManageSubscription_Start', 'Start:')} {formatDateByPrefs(data.planStartDate, dateFormat, timezoneOffset)}
                  </span>
                )}
                {data.periodEndDate && (
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                    {t('ManageSubscription_End', 'End:')} {formatDateByPrefs(data.periodEndDate, dateFormat, timezoneOffset)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="mt-5">
            <UsageBar label={t('ManageSubscription_Mailboxes', 'Mailboxes')} current={data.mailboxCount} max={data.mailboxLimit} />
            {subscriptionState !== 'Expired' && subscriptionState !== 'Blocked' && data.mailboxLimit > 0 && data.mailboxCount >= data.mailboxLimit && (
              <div className="flex items-start gap-2 p-3 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/10 dark:border-amber-800/40 dark:text-amber-300" role="alert">
                <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="m-0">
                  {t('ManageSubscription_MailboxLimitBanner_Body', 'You have reached your mailbox limit. To create additional mailboxes, upgrade to a higher plan. View upgrade options below.')}
                </p>
              </div>
            )}
            <UsageBar label={t('ManageSubscription_KCs', 'KCs')} current={data.kcCount} max={data.kcLimit} />
            {subscriptionState !== 'Expired' && subscriptionState !== 'Blocked' && data.kcLimit > 0 && data.kcCount >= data.kcLimit && (
              <div className="flex items-start gap-2 p-3 mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/10 dark:border-amber-800/40 dark:text-amber-300" role="alert">
                <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="m-0">
                  {t('ManageSubscription_KcLimitBanner_Body', 'You have reached your KCs limit. To support additional trading partners, upgrade to a higher plan. View upgrade options below.')}
                </p>
              </div>
            )}
          </div>

          {data.referralCode && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('ManageSubscription_AccessCode', 'Referral Code')}:</span>
              <span className="font-mono font-semibold tracking-wider text-primary">{data.referralCode}</span>
            </div>
          )}
        </SectionCard>

        {/* ── Billing Management ── */}
        {data.customerId && data.stripeCusPortalUrl && (
          <SectionCard>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <SectionHeading className="mb-1">{t('ManageSubscription_BillingTitle', 'Billing & Payment')}</SectionHeading>
                <p className="text-sm text-muted-foreground m-0">
                  {t('ManageSubscription_BillingDescription', 'Manage payment method, invoices, and billing details.')}
                </p>
              </div>
              <a
                href={data.stripeCusPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg no-underline text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer shrink-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                {t('ManageSubscription_OpenBillingPortal', 'Open Billing Portal')}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            </div>
          </SectionCard>
        )}

        {/* ── Upgrade Options ── */}
        {(hasUpgradeOptions || isProPlus) && (
          <div>
            <h2 className="text-base font-semibold mb-4">{t('ManageSubscription_UpgradeTitle', 'Upgrade Your Plan')}</h2>
            {isProPlus ? (
              /* Pro Plus: Contact Sales block (no upgrade cards) */
              <div className="flex items-start gap-3 p-4 rounded-lg text-sm border bg-primary/5 border-primary/20 text-primary" role="alert">
                <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold m-0 mb-1">
                    {t('ManageSubscription_ProPlus_ContactSalesTitle', 'You are on the highest self-service tier')}
                  </p>
                  <p className="m-0 mb-3">
                    {t('ManageSubscription_ProPlus_ContactSalesBody', 'If your production workload requires higher KC volume, additional mailboxes, or custom contract terms, our Sales team can help.')}
                  </p>
                  <a
                    href={t('ManageSubscription_SalesMeetingUrl', 'https://outlook.office365.com/book/BookaMeetingwithECGridSalesToday@ecgrid.com/s/DkyrhyhCoka9nRg1dsJlvg2?ismsaljsauthenabled=true')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg no-underline text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t('ManageSubscription_ScheduleConversation', 'Schedule a conversation')}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                </div>
              </div>
            ) : (
              <>
                {/* Upgrade intro copy with calendar link */}
                <p className="text-sm text-muted-foreground italic mb-4">
                  {t('ManageSubscription_UpgradeIntro', 'Upgrades take effect immediately. When your new plan is confirmed, your current subscription is cancelled automatically. You will not be double-billed. Interested in an annual plan or volume pricing?')}{' '}
                  <a
                    href={t('ManageSubscription_SalesMeetingUrl', 'https://outlook.office365.com/book/BookaMeetingwithECGridSalesToday@ecgrid.com/s/DkyrhyhCoka9nRg1dsJlvg2?ismsaljsauthenabled=true')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium not-italic underline underline-offset-2"
                  >
                    {t('ManageSubscription_ContactSalesTeam', 'Contact our sales team')} →
                  </a>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <CurrentPlanCard
                    displayName={data.planDisplayName}
                    priceLabel={data.planPriceLabel ?? ''}
                    mailboxLimit={data.mailboxLimit}
                    kcLimit={data.kcLimit}
                    kcOverageRate={data.kcOverageRate ?? 0}
                    hasApiAccess={data.hasApiAccess ?? false}
                    hasWhiteLabel={data.hasWhiteLabel ?? false}
                    hasRealTimeTracking={data.hasRealTimeTracking ?? false}
                    currentBadgeText={t('ManageSubscription_CurrentBadge', 'Current Plan')}
                    mailboxesLabel={t('ManageSubscription_UptoMailboxes', 'Up to {{count}} Mailboxes', { count: data.mailboxLimit })}
                    kcsLabel={t('ManageSubscription_UptoKCs', 'Up to {{count}} KCs', { count: data.kcLimit })}
                    kcOverageLabel={t('ManageSubscription_KcOverage', 'KC overage')}
                    fullApiAccessLabel={t('ManageSubscription_FullApiAccess', 'Full API access')}
                    whiteLabelLabel={t('ManageSubscription_WhiteLabelPortal', 'White-label portal')}
                    realTimeTrackingLabel={t('ManageSubscription_RealTimeTracking', 'Real-time tracking')}
                  />
                  {data.upgradeOptions.map((opt) => (
                    <UpgradeCard
                      key={opt.planId}
                      planId={opt.planId}
                      displayName={opt.displayName}
                      mailboxLimit={opt.mailboxLimit}
                      kcLimit={opt.kcLimit}
                      priceLabel={opt.priceLabel}
                      kcOverageRate={opt.kcOverageRate}
                      upgrading={upgrading}
                      onUpgrade={initiateUpgrade}
                      mailboxesLabel={t('ManageSubscription_UptoMailboxes', 'Up to {{count}} Mailboxes', { count: opt.mailboxLimit })}
                      kcsLabel={t('ManageSubscription_UptoKCs', 'Up to {{count}} KCs', { count: opt.kcLimit })}
                      kcOverageLabel={t('ManageSubscription_KcOverage', 'KC overage')}
                      fullApiAccessLabel={t('ManageSubscription_FullApiAccess', 'Full API access')}
                      whiteLabelLabel={t('ManageSubscription_WhiteLabelPortal', 'White-label portal')}
                      realTimeTrackingLabel={t('ManageSubscription_RealTimeTracking', 'Real-time tracking')}
                      processingText={t('ManageSubscription_Processing', 'Processing...')}
                      tryNowText={t('ManageSubscription_TryNow', 'Try Now')}
                      upgradeNowText={t('ManageSubscription_UpgradeNow', 'Upgrade Now')}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Payment History ── */}
        {data.paymentHistory && data.paymentHistory.length > 0 && (
          <SectionCard className="overflow-x-auto">
            <SectionHeading className="mb-4">{t('ManageSubscription_PaymentHistory', 'Payment History')}</SectionHeading>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('ManageSubscription_ColDate', 'Date')}</th>
                  <th className="text-left py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('ManageSubscription_ColType', 'Type')}</th>
                  <th className="text-left py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('ManageSubscription_ColDescription', 'Description')}</th>

                </tr>
              </thead>
              <tbody>
                {data.paymentHistory.map((entry, idx) => (
                  <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                    <td className="py-2.5 tabular-nums">
                      {formatDateByPrefs(entry.date, dateFormat, timezoneOffset)}
                    </td>
                    <td className="py-2.5">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-primary text-primary-foreground">
                        {entry.type}
                      </span>
                    </td>
                    <td className="py-2.5">{entry.description}</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}
      </div>
    </PortalLayout>
  );
});

ManageSubscriptionPage.displayName = 'ManageSubscriptionPage';
